/**
 * Thin wrapper around Zebra's Browser Print JavaScript SDK.
 *
 * Browser Print is a small Zebra desktop app the customer installs on the same Windows machine as the
 * browser. It runs a local service that the SDK (window.BrowserPrint) talks to in order to enumerate and
 * print to printers the app knows about (network printers added by IP, or USB-attached). The dashboard
 * never addresses the printer's IP itself - that is what lets a cloud-hosted page print to a printer on
 * the customer's LAN without any inbound network access or VPN.
 *
 * The official SDK is a static file we vendor at public/vendor/BrowserPrint-3.1.250.min.js (see the
 * README there). It is loaded lazily the first time a user prints, so it never slows the rest of the app.
 */

const SDK_SRC = `${import.meta.env.BASE_URL}vendor/BrowserPrint-3.1.250.min.js`;
const LAST_PRINTER_KEY = 'agyle.zebra.lastPrinterUid';

/**
 * Local-dev testing aid: with VITE_ZEBRA_CONSOLE_PRINT=true in .env (and running `npm start`),
 * getPrinters() skips Browser Print and returns these fake printers, which log the ZPL to the console
 * instead of printing. The names carry a DPI token so the backend's DPI scaling can be exercised too.
 * Ignored in any production build (test and prod deploys), even if the variable is set.
 */
const CONSOLE_PRINT_ENABLED = import.meta.env.DEV
  && import.meta.env.VITE_ZEBRA_CONSOLE_PRINT === 'true';

const CONSOLE_PRINTERS = [203, 300].map(dpi => ({
  uid: `console-${dpi}dpi`,
  name: `Console-${dpi}dpi`,
  send(zpl, onSuccess) {
    // eslint-disable-next-line no-console
    console.log(`[Zebra console printer ${dpi}dpi]\n${zpl}`);
    onSuccess();
  }
}));

let sdkPromise = null;

/** Errors users can act on (SDK missing, service down, printer offline) versus unexpected failures. */
export class BrowserPrintError extends Error {}

/**
 * Loads the Browser Print SDK script once and resolves with the global BrowserPrint object. A failed
 * load is not cached, so a later attempt (e.g. after the user installs Browser Print) can succeed.
 */
export function loadBrowserPrint() {
  if (window.BrowserPrint) {
    return Promise.resolve(window.BrowserPrint);
  }
  if (sdkPromise) {
    return sdkPromise;
  }

  sdkPromise = new Promise((resolve, reject) => {
    const onLoad = () => window.BrowserPrint
      ? resolve(window.BrowserPrint)
      : reject(new BrowserPrintError(
        'The Browser Print SDK did not load. Confirm public/vendor/BrowserPrint-3.1.250.min.js exists in '
        + 'this build (in local dev a missing file is served as the app HTML, which fails to parse).'));
    const onError = () => reject(new BrowserPrintError(
      'Could not load the Browser Print SDK from this dashboard build ' +
      '(public/vendor/BrowserPrint-3.1.250.min.js).'));

    // Drop any tag left by a previous failed attempt: it has already fired its load/error event, so a
    // fresh tag is the only reliable way to get a new one (otherwise a retry would hang forever).
    const stale = document.querySelector('script[data-zebra-browser-print]');
    if (stale) {
      stale.parentNode.removeChild(stale);
    }

    const script = document.createElement('script');
    script.src = SDK_SRC;
    script.async = true;
    script.setAttribute('data-zebra-browser-print', '');
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    document.body.appendChild(script);
  });

  // Let a failed load be retried on the next attempt rather than caching the rejection forever.
  sdkPromise.catch(() => { sdkPromise = null; });
  return sdkPromise;
}

/**
 * Returns the printers the local Browser Print service knows about, plus the uid of the default printer
 * (for pre-selection). Rejects with a BrowserPrintError if the SDK or the local service is unavailable.
 */
export async function getPrinters() {
  if (CONSOLE_PRINT_ENABLED) {
    return {devices: CONSOLE_PRINTERS, defaultUid: CONSOLE_PRINTERS[0].uid};
  }

  const browserPrint = await loadBrowserPrint();

  const devices = await new Promise((resolve, reject) => {
    browserPrint.getLocalDevices(
      result => resolve(normalizeDeviceList(result)),
      () => reject(new BrowserPrintError(
        'Could not reach Browser Print. Make sure the Browser Print app is running on this computer.')),
      'printer'
    );
  });

  const defaultUid = await new Promise(resolve => {
    try {
      browserPrint.getDefaultDevice(
        'printer',
        device => resolve(device ? device.uid : null),
        () => resolve(null)
      );
    }
    catch (e) {
      resolve(null);
    }
  });

  return {devices, defaultUid};
}

/**
 * Sends a raw ZPL string to a Browser Print device. Resolves on success, rejects with a BrowserPrintError
 * carrying the service's message (e.g. the printer is offline).
 */
export function printZpl(device, zpl) {
  return new Promise((resolve, reject) => {
    device.send(
      zpl,
      () => resolve(),
      error => reject(new BrowserPrintError(typeof error === 'string' && error
        ? error
        : 'The printer could not be reached. Check that it is on and connected to the network.'))
    );
  });
}

export function getRememberedPrinterUid() {
  try {
    return window.localStorage.getItem(LAST_PRINTER_KEY);
  }
  catch (e) {
    return null;
  }
}

export function rememberPrinterUid(uid) {
  try {
    window.localStorage.setItem(LAST_PRINTER_KEY, uid);
  }
  catch (e) {
    // localStorage can be unavailable (e.g. private mode); remembering the printer is best-effort.
  }
}

/** getLocalDevices has returned both a bare array and a {printer: [...]} shape across versions. */
function normalizeDeviceList(result) {
  if (Array.isArray(result)) {
    return result;
  }
  if (result && Array.isArray(result.printer)) {
    return result.printer;
  }
  return [];
}
